import { useMemo, useState } from "react";
import { useCoffees } from "../hooks/useCoffees";
import { useRoasterColors, indexByRoaster } from "../hooks/useRoasterColors";
import { api } from "../lib/api";
import { KIND_OPTIONS, kindLabel } from "../types";

// Default colors for unset roasters — chosen so the picker opens to a sensible
// neutral instead of black/black, which would render the roaster line invisible.
const DEFAULT_BG = "#f5e6d3";
const DEFAULT_TEXT = "#222222";

export function Roasters() {
  const { data: coffees } = useCoffees();
  const { data: colors, refresh } = useRoasterColors();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [kind, setKind] = useState("coffee");

  // Distinct roasters present in the active inventory (filtered by kind),
  // sorted A-Z.
  const roasters = useMemo(() => {
    const set = new Set<string>();
    for (const c of coffees || []) {
      if (c.archived) continue;
      if (kind && c.kind !== kind) continue;
      const name = c.roaster?.trim();
      if (name) set.add(name);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [coffees, kind]);

  const colorMap = indexByRoaster(colors);

  const save = async (roaster: string, bg: string, text: string, bold: boolean, italic: boolean) => {
    setBusy(roaster); setError(null);
    try {
      await api.setRoasterColor(roaster, bg, text, bold, italic);
      refresh();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const clear = async (roaster: string) => {
    setBusy(roaster); setError(null);
    try {
      await api.deleteRoasterColor(roaster);
      refresh();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-6">
      <header className="space-y-2">
        <div className="eyebrow">Setup · Label theme</div>
        <h1 className="font-display text-3xl">Roaster colors</h1>
        <p className="text-sm text-paper-600 max-w-xl">
          Set a background + text color for each roaster. The roaster line on every
          printed label uses these colors so the right coffee jumps out when you're
          rummaging in the freezer. Unset roasters print plain.
        </p>
      </header>

      <div className="flex flex-wrap gap-2">
        <KindChip label="All" active={kind === ""} onClick={() => setKind("")} />
        {KIND_OPTIONS.map((k) => (
          <KindChip key={k} label={kindLabel(k)} active={kind === k} onClick={() => setKind(k)} />
        ))}
      </div>

      {error ? <div className="border border-red-300 bg-red-50 text-red-800 px-3 py-2 text-sm">{error}</div> : null}

      <ul className="border-t border-paper-200">
        {roasters.length === 0 ? (
          <li className="py-4 text-sm text-paper-500">No roasters in inventory yet.</li>
        ) : null}
        {roasters.map((r) => {
          const cur = colorMap.get(r);
          return (
            <RoasterRow
              key={r}
              name={r}
              initialBg={cur?.bg ?? DEFAULT_BG}
              initialText={cur?.text ?? DEFAULT_TEXT}
              initialBold={cur?.bold ?? false}
              initialItalic={cur?.italic ?? false}
              hasColor={!!cur}
              busy={busy === r}
              onSave={(bg, text, bold, italic) => save(r, bg, text, bold, italic)}
              onClear={() => clear(r)}
            />
          );
        })}
      </ul>
    </div>
  );
}

function RoasterRow({ name, initialBg, initialText, initialBold, initialItalic, hasColor, busy, onSave, onClear }: {
  name: string;
  initialBg: string;
  initialText: string;
  initialBold: boolean;
  initialItalic: boolean;
  hasColor: boolean;
  busy: boolean;
  onSave: (bg: string, text: string, bold: boolean, italic: boolean) => void;
  onClear: () => void;
}) {
  const [bg, setBg] = useState(initialBg);
  const [text, setText] = useState(initialText);
  const [bold, setBold] = useState(initialBold);
  const [italic, setItalic] = useState(initialItalic);
  const dirty =
    bg.toLowerCase() !== initialBg.toLowerCase() ||
    text.toLowerCase() !== initialText.toLowerCase() ||
    bold !== initialBold ||
    italic !== initialItalic;
  return (
    <li className="flex flex-wrap items-center gap-3 py-3 border-b border-paper-200">
      <div className="min-w-0 flex-1 basis-48">
        <div
          className="inline-block px-2 py-1 text-sm border border-paper-200"
          style={{ background: bg, color: text, fontWeight: bold ? 700 : undefined, fontStyle: italic ? "italic" : undefined }}
        >
          {name}
        </div>
        {hasColor ? null : <span className="ml-2 text-[10px] uppercase tracking-widest text-paper-400">unset</span>}
      </div>
      <label className="flex items-center gap-2 text-xs text-paper-600">
        Bg
        <input type="color" value={bg} onChange={(e) => setBg(e.target.value)} className="w-10 h-8 border border-paper-300 bg-transparent" />
      </label>
      <label className="flex items-center gap-2 text-xs text-paper-600">
        Text
        <input type="color" value={text} onChange={(e) => setText(e.target.value)} className="w-10 h-8 border border-paper-300 bg-transparent" />
      </label>
      <button
        type="button"
        onClick={() => setBold((b) => !b)}
        className={`px-2 h-8 text-sm font-bold border ${bold ? "bg-ink text-paper-50 border-ink" : "bg-paper-50 text-paper-600 border-paper-300 hover:border-ink hover:text-ink"}`}
        aria-pressed={bold}
        title="Bold"
      >B</button>
      <button
        type="button"
        onClick={() => setItalic((i) => !i)}
        className={`px-2 h-8 text-sm italic border ${italic ? "bg-ink text-paper-50 border-ink" : "bg-paper-50 text-paper-600 border-paper-300 hover:border-ink hover:text-ink"}`}
        aria-pressed={italic}
        title="Italic"
      >I</button>
      <button
        className="btn-primary"
        disabled={busy || !dirty}
        onClick={() => onSave(bg, text, bold, italic)}
      >
        {busy ? "Saving…" : hasColor ? "Update" : "Save"}
      </button>
      <button
        className="btn-ghost"
        disabled={busy || !hasColor}
        onClick={onClear}
        title="Reset to plain (no color)"
      >
        Clear
      </button>
    </li>
  );
}

function KindChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1 text-xs uppercase tracking-widest border ${active ? "bg-ink text-paper-50 border-ink" : "bg-paper-50 text-paper-600 border-paper-300 hover:border-ink hover:text-ink"}`}
    >
      {label}
    </button>
  );
}
